<template>
    <app-wrapper>
        <app-header :loan="loan" :page="'Loan'" medium-max-width="max-w-1000" short-top-header>

            <template #navigation/>

            <template #top-top>
                <div class="flex justify-between items-center">
                    <p class="text-sm font-semibold">Loan #{{ loan.id }}</p>
                    <loan-status-tag :loan="loan"/>
                </div>
            </template>

            <template #top-bottom>
                <LoanBalance :loan="loan"/>
            </template>

            <template #bottom-left>
                <LoanSummaryLeft :loan="loan"/>
            </template>

            <template #bottom-right>
                <LoanSummaryRight :loan="loan"/>
            </template>

            <template #bottom-bottom>
                <div v-if="loan.eligibility_additional_credit && ((loan.eligibility_additional_credit.criteria.after_payments > postedPayments.length) || ! applyMoreCredit)">
                    <div v-if="loan.eligibility_additional_credit.criteria.after_payments > postedPayments.length" class="flex border-t border-solid border-color-[#DEE8F9] justify-between pt-3.5">
                        <div>
                            Eligible for more credit
                        </div>
                        <div class="font-semibold text-right">
                            in {{ loan.eligibility_additional_credit.criteria.after_payments - postedPayments.length }} payments
                        </div>
                    </div>
                    <a v-else-if="! applyMoreCredit" href="#" @click.prevent="applyMoreCredit = true" class="block w-full no-underline mt-2 bg-action rounded text-white text-center text-lg font-semibold py-3">
                        Apply for More Credit
                    </a>
                </div>

                <LoanAccountClosed :loan="loan"/>

                <ReApplyButton v-if="loan.status === 'pay_in_full_posted'" :loan="loan" />

                <LoanPaymentsDeactivated :loan="loan"/>

                <LoanAuthorizePayment :loan="loan"/>
            </template>

        </app-header>
        <app-body medium-max-width="max-w-1000" footer short>

            <template #main>

                <template v-if="loan.eligibility_additional_credit && applyMoreCredit">
                    <div class="p-4 bg-white border border-gray-300 rounded-lg mb-4">
                        <div class="w-full mb-3.5">
                            <label for="amount" class="font-semibold">I'd like to borrow an additional:</label>
                        </div>
                        <div class="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                            <select v-model="moreCreditAmount" id="amount" name="amount" class="dropdown-select w-full border-0 text-xl bg-gray-100">
                                <template v-for="(enabled, amount) in loan.eligibility_additional_credit.additional_amounts">
                                    <option v-if="enabled" :value="amount">${{ amount }}</option>
                                </template>
                            </select>
                            <div class="flex-initial">
                                <form @submit.prevent="applyForAdditionalCredit" method="POST">
                                    <button type="submit" :disabled="isApplyingForCredit" class="block w-full no-underline bg-action rounded text-white text-center text-lg font-semibold py-3 px-4 disabled:opacity-50 disabled:cursor-not-allowed">
                                        <span v-if="!isApplyingForCredit">Apply</span>
                                        <span v-else>Applying...</span>
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </template>

                <!-- Paused Loan Message -->
                <div v-if="loan.status === 'paused'" class=" bg-white cursor-pointer border rounded-lg border-blue-300 mb-4">
                    <div class="flex justify-between items-center bottom-line p-4">
                        <h6 class="text-base font-semibold">Loan Paused</h6>
                    </div>
                    <div class="p-4">
                        <p class="text-gray-700">
                            This loan is currently paused. For more information, please contact support at 
                            <a :href="`mailto:${supportEmail}`" class="text-primary-700 underline">{{ supportEmail }}</a>.
                        </p>
                    </div>
                </div>

                <div v-if="pastPastDuePayments.length > 0 && loan.status !== 'paused'" class="cursor-pointer border rounded-lg bg-red-50 border-red-300 mb-4">
                    <div class="flex justify-between items-center bottom-line p-4">
                        <h6 class="text-base font-semibold">Missed Payments</h6>
                    </div>
                    <div class="p-4 flex justify-between items-center">
                        <p>
                            {{ pastPastDuePayments[0].due_at | date('dddd, MMM D') }}<br/>
                            <span class="block font-semibold mt-1">
                                {{ pastPastDuePayments[0].computed_amount | money(2) }}
                            </span>
                        </p>
                        <p class="font-medium">
                            <a :href="`/account/loans/${loan.id}/recover-payment/${pastPastDuePayments[0].id}`"
                               @click="onPayNowClick(pastPastDuePayments[0].id)"
                               class="block no-underline border border-primary-700 rounded text-primary-700 text-center font-semibold px-3 py-2">
                                Pay Now
                            </a>
                        </p>
                    </div>
                </div>

                <div class="flex flex-col md:flex-row md:flex-wrap space-y-3 md:space-y-0">
                    <div class="w-full md:w-1/2">

                        <div v-if="isLoanActive && loan.status !== 'pay_in_full_processing'" class="cursor-pointer border md:mr-2 rounded-lg bg-gray-50 border-gray-200">
                            <div class="flex justify-between items-center bottom-line p-4">
                                <h6 class="text-base font-semibold">Upcoming Payments</h6>

                                <a v-if="futurePendingPayments.length > 5 && !showAllFuturePayments" @click.prevent="showAllFuturePayments = true" class="cursor-pointer font-semibold no-underline text-sm">
                                    See all
                                </a>
                                <a v-else-if="futurePendingPayments.length > 5 && showAllFuturePayments" @click.prevent="showAllFuturePayments = false" class="cursor-pointer font-semibold no-underline text-sm">
                                    Hide
                                </a>
                            </div>
                            <div v-for="payment in limitedFuturePayments" :key="payment.id" class="bottom-line p-4 flex flex-wrap justify-between items-center">
                                <p style="order: 1">
                                    {{ payment.due_at | date('dddd, MMM D') }}
                                    <span v-if="payment.status === 'past_due'" class="text-danger font-semibold">(Past Due)</span>
                                </p>
                                <p style="order: 3" class="font-medium">{{ payment.computed_amount | money(2) }}</p>
                                <ChangePaymentDueDate 
                                    v-if="payment.can_change_due_date" 
                                    :payment="payment" 
                                    :frequency="loan.frequency"
                                    :number-of-moved-payments-to-end="loan.number_of_moved_payment_to_end"
                                    @due-date-changed="dueDateChanged"
                                />
                            </div>
                        </div>

                        <!-- PayFull -->
                        <FullPayment :loan="loan" v-if="['current', 'pay_in_full_processing'].includes(loan.status)"/>

                        <EarlyPaymentForm :loan="loan" />

                    </div>
                    <div v-if="loan.status !== 'paused'" class="w-full md:w-1/2">

                        <div class="cursor-pointer border md:ml-2 rounded-lg bg-gray-50 border-gray-200">
                            <div class="flex justify-between items-center bottom-line p-4">
                                <h6 class="text-base font-semibold">Payment History</h6>

                                <a v-if="pastPayments.length > 3 && !showAllPastPayments" @click.prevent="expandPaymentHistory" class="cursor-pointer font-semibold no-underline text-sm">
                                    See all
                                </a>
                                <a v-else-if="pastPayments.length > 3 && showAllPastPayments" @click.prevent="showAllPastPayments = false" class="cursor-pointer font-semibold no-underline text-sm">
                                    Hide
                                </a>                                
                            </div>
                            
                            <!-- Paid in Full Entry -->
                            <div v-if="['closed', 'closed_remarketable'].includes(loan.status) && loan.pif_date" class="bottom-line p-4 flex justify-between items-center bg-green-50">
                                <p class="inline-flex items-center">
                                    <i class="fa fa-check-circle text-success text-3xl"/>
                                    &nbsp;&nbsp;<span>
                                        {{ loan.pif_date | date('dddd, MMM Do') }}<br/>
                                        <span class="text-sm font-semibold text-green-700 block mt-1">Paid in Full</span>
                                    </span>
                                </p>
                                <p class="font-medium text-green-700">{{ loan.pif_amount | money(2) }}</p>
                            </div>
                            
                            <div v-for="payment in limitedPastPayments" :key="payment.id" class="bottom-line p-4 flex justify-between items-center">
                                <p class="inline-flex items-center">
                                    <i v-if="payment.status === 'posted'" class="fa fa-check-circle text-success text-3xl"/>
                                    <i v-else-if="payment.status === 'past_due'" class="fas fa-exclamation-circle text-danger text-3xl"/>
                                    <i v-else-if="payment.status === 'processed'" class="fa fa-circle-notch fa-spin text-3xl"/>
                                    <i v-else-if="payment.status === 'recovered'" class="fa fa-check-circle text-success text-3xl"/>
                                    <i v-else-if="payment.status === 'forgiven'" class="fa fa-angle-double-down text-success text-3xl"/>
                                    <i v-else-if="payment.status === 'failed'" class="fa fa-exclamation-circle text-danger text-3xl"/>
                                    <i v-else-if="payment.status === 'pending'" class="fa fa-clock text-3xl"/>
                                    &nbsp;&nbsp;{{ payment.due_at | date('dddd, MMM Do') }}
                                </p>
                                <p class="font-medium">{{ payment.computed_amount | money(2) }}</p>
                            </div>
                            <div v-if="! pastPayments.length && ! (['closed', 'closed_remarketable'].includes(loan.status) && loan.pif_date)" class="p-4 flex justify-between items-center">
                                <p class="inline-flex items-center">
                                    No past payments.
                                </p>
                            </div>
                            <!--
                            <div v-if="pastPayments.length || (['closed', 'closed_remarketable'].includes(loan.status) && loan.pif_date)" class="px-4 py-3.5">
                                <a :href="`/account/loans/${loan.id}/payments`" class="block w-full no-underline bg-gray-50 border border-primary-700 rounded text-primary-700 text-center text-lg font-semibold py-3">
                                    View Payments
                                </a>
                            </div>
                            -->
                        </div>

                    </div>
                </div>
            </template>
        </app-body>

        <!-- error message -->
        <div v-if="errorMessage" class="bg-red-50 border border-red-300 rounded-lg p-4 absolute right-0 top-0 w-full">
            <p class="text-red-700">{{ errorMessage }}</p>
        </div>
    </app-wrapper>
</template>

<script>
import ActionPayOffLoan from '../../app/ActionPayOffLoan';
import LoanStatusTag from "../../app/LoanStatusTag.vue";
import LoanBalance from "../../components/LoanBalance.vue";
import LoanSummaryLeft from "../../components/LoanSummaryLeft.vue";
import LoanSummaryRight from "../../components/LoanSummaryRight.vue";
import LoanAccountClosed from "../../components/LoanAccountClosed.vue";
import LoanPaymentsDeactivated from "../../components/LoanPaymentsDeactivated.vue";
import LoanAuthorizePayment from "../../components/LoanAuthorizePayment.vue";
import ChangePaymentDueDate from '../../components/ChangePaymentDueDate.vue';
import FullPayment from './payments/FullPayment.vue';
import EarlyPaymentForm from './payments/EarlyPaymentForm.vue';
import ReApplyButton from '../../components/ReApplyButton.vue';

export default {
    components: {
        LoanBalance,
        LoanSummaryLeft,
        LoanSummaryRight,
        LoanAccountClosed,
        LoanPaymentsDeactivated,
        LoanAuthorizePayment,
        LoanStatusTag,
        ActionPayOffLoan,
        ChangePaymentDueDate,
        FullPayment,
        EarlyPaymentForm,
        ReApplyButton,
    },

    mixins: [
        require('../../mixins/InteractsWithGtag').default,
        require('../../mixins/InteractsWithLoans').default
    ],

    computed: {
        /**
         * @returns {Array}
         */
        limitedFuturePayments() {
            return this.showAllFuturePayments ? this.futurePendingPayments : this.futurePendingPayments.slice(0, 5);
        },        
        limitedPastPayments() {
            return this.showAllPastPayments ? this.pastPayments : this.pastPayments.slice(-3);
        }
    },

    mounted() {
        // Track dashboard loaded
        window.LogRocket?.track?.('dashboard_loaded', {
            loan_id: this.loan?.id,
            loan_status: this.loan?.status,
            balance: this.loan?.balance
        });

        if (this.loan.eligibility_additional_credit) {
            const enabledAmounts = [];

            for (const key of Object.keys(this.loan.eligibility_additional_credit.additional_amounts)) {
                if (this.loan.eligibility_additional_credit.additional_amounts[key]) {
                    enabledAmounts.push(key);
                }
            }

            this.moreCreditAmount = enabledAmounts.sort((a, b) => a - b)[0];
        }


        const urlParams = new URLSearchParams(window.location.search);
        const myParam = urlParams.get('apply-for-more');

        this.applyMoreCredit = myParam === 'true';
    },

    data() {
        return {
            future: true,
            applyMoreCredit: false,
            moreCreditAmount: null,
            gTagPageName: 'loan-dashboard',
            showAllFuturePayments: false,
            showAllPastPayments: false,
            isApplyingForCredit: false,
            supportEmail: window.support_email,
            errorMessage: null,            
        }
    },

    methods: {
        applyForAdditionalCredit() {
            // Track additional credit application
            window.LogRocket?.track?.('additional_credit_requested', {
                loan_id: this.loan.id,
                amount: this.moreCreditAmount
            });

            this.isApplyingForCredit = true;
            this.$http.post(`/reapply/additional-credit/${this.loan.id}`, {
                amount: this.moreCreditAmount,
                verify: true
            }).then(({data}) => {
                window.location = '/account/dashboard';
            }).catch(({response}) => {
                this.errorMessage = response.data.error;
                this.isApplyingForCredit = false;
            });
        },
        dueDateChanged(payment) {
            // const payment = this.futurePendingPayments.find(p => p.id === payment.id);
            // payment.due_at = '2024-10-02T00:00:00.000000Z';
            // payment.can_change_due_date = false;
        },

        onPayNowClick(paymentId) {
            window.LogRocket?.track?.('payment_initiated', {
                loan_id: this.loan.id,
                source: 'dashboard_missed_payment',
                payment_id: paymentId
            });
        },

        expandPaymentHistory() {
            this.showAllPastPayments = true;
            window.LogRocket?.track?.('payment_history_viewed', {
                loan_id: this.loan.id,
                action: 'expand'
            });
        }
    }
}
</script>
